/**
 * lib/ninetailed/resolveMemberVariant.ts
 *
 * Pure, defensive helper that extracts the content to show a KNOWN member during
 * the `<Experience>` loading window, so a logged-in visitor never sees the
 * logged-out baseline flash before Ninetailed resolves the visitor profile.
 *
 * It returns the FIRST variant of the FIRST experience that has one — matching the
 * provisioned single-trait (`isMember`) EntryReplacement model (one member
 * experience per banner, 1:1 slide swap). The variant entries are full content
 * entries with the same presentational fields as the baseline, so the returned
 * object drops straight into the wrapped presentational component.
 *
 * Defensive by design (mirrors `mapExperiences`): Contentful data can be partially
 * authored, unpublished, or malformed, so on ANY unexpected input this returns
 * `null` (→ fall back to the baseline) and NEVER throws.
 */

type RawVariant =
  | { sys?: { id?: string | null } | null; [key: string]: unknown }
  | null;

type RawExperience = {
  ntVariantsCollection?: { items?: Array<RawVariant> | null } | null;
} | null;

function toItemsArray(input: unknown): Array<RawExperience> | null {
  if (Array.isArray(input)) return input as Array<RawExperience>;
  if (input && Array.isArray((input as { items?: unknown }).items)) {
    return (input as { items: Array<RawExperience> }).items;
  }
  return null;
}

/**
 * Resolve the member variant's presentational fields from the raw experiences
 * collection, with its `id` set to the variant entry's `sys.id`. Returns `null`
 * when there is no usable variant.
 */
export function resolveMemberVariant(
  input: unknown,
): Record<string, unknown> | null {
  try {
    const experiences = toItemsArray(input);
    if (!experiences) return null;

    for (const experience of experiences) {
      const variants = experience?.ntVariantsCollection?.items;
      if (!Array.isArray(variants)) continue;

      const variant = variants.find(
        (v): v is Exclude<RawVariant, null> => !!v && typeof v === 'object',
      );
      if (!variant) continue;

      const id =
        typeof variant.sys?.id === 'string' ? variant.sys.id : undefined;
      return id ? { ...variant, id } : { ...variant };
    }

    return null;
  } catch {
    return null;
  }
}
